import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { PLATFORM_FEE_PERCENT } from '@/lib/constants'

// ─── Class name helper ───────────────────────────────────────────────────────

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// ─── Currency ────────────────────────────────────────────────────────────────

/**
 * Formats a numeric value as Brazilian Real.
 * e.g. 1500 → "R$ 1.500,00"
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// ─── Dates ───────────────────────────────────────────────────────────────────

/**
 * Formats a date string or Date object.
 * Default format: 'dd/MM/yyyy'
 */
export function formatDate(
  date: string | Date,
  dateFormat: string = 'dd/MM/yyyy',
): string {
  const parsed = typeof date === 'string' ? parseISO(date) : date
  return format(parsed, dateFormat, { locale: ptBR })
}

/**
 * Formats a date string or Date object with time.
 * e.g. "30/04/2026 às 14:30"
 */
export function formatDateTime(date: string | Date): string {
  return formatDate(date, "dd/MM/yyyy 'às' HH:mm")
}

// ─── String helpers ──────────────────────────────────────────────────────────

/**
 * Returns up to 2 initials from a full name.
 * e.g. "João Silva" → "JS" | "Ana" → "A"
 */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const first = words[0][0] ?? ''
  const second = words.length > 1 ? words[words.length - 1][0] ?? '' : ''
  return (first + second).toUpperCase()
}

/**
 * Converts a string to a URL-friendly slug.
 * e.g. "Direção Defensiva" → "direcao-defensiva"
 */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')    // remove non-alphanumeric
    .replace(/\s+/g, '-')             // spaces → hyphens
    .replace(/-+/g, '-')              // collapse multiple hyphens
    .replace(/^-|-$/g, '')            // trim leading/trailing hyphens
}

// ─── Geo ─────────────────────────────────────────────────────────────────────

/**
 * Calculates the great-circle distance between two coordinates using the
 * Haversine formula. Returns distance in kilometres.
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371 // Earth's radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180

  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// ─── Platform fees ───────────────────────────────────────────────────────────
//
// A taxa é um ACRÉSCIMO sobre o preço do instrutor: ele cobra R$ 60, o aluno
// paga R$ 84 (60 + 40 %) e o instrutor recebe os R$ 60 inteiros. Todas as
// funções abaixo recebem o valor DO INSTRUTOR. Espelha `_shared/pricing.ts`.

/** Preço ao aluno: valor do instrutor + PLATFORM_FEE_PERCENT. */
export function getStudentPrice(instructorAmount: number): number {
  return round2(instructorAmount * (1 + PLATFORM_FEE_PERCENT / 100))
}

/** Parte da plataforma: o que o aluno paga além do valor do instrutor. */
export function getPlatformFee(instructorAmount: number): number {
  return round2(getStudentPrice(instructorAmount) - instructorAmount)
}

/** Repasse ao instrutor: o valor dele, inteiro. */
export function getInstructorPayout(instructorAmount: number): number {
  return round2(instructorAmount)
}

/** Arredonda para 2 casas, evitando centavos fantasma de ponto flutuante. */
export function round2(value: number): number {
  return Math.round(value * 100) / 100
}

// ─── Rating ──────────────────────────────────────────────────────────────────

/** Formats a rating number to one decimal place. e.g. 4.833 → "4.8" */
export function formatRating(rating: number): string {
  return rating.toFixed(1)
}

// ─── Booking status ──────────────────────────────────────────────────────────

/**
 * Returns a human-readable Portuguese label for a booking status.
 *
 * As chaves espelham o enum `booking_status` do Postgres. Manter em sincronia
 * com `BookingStatus` em `@/types/database` — um status não mapeado vaza o
 * valor cru do enum para a UI.
 */
export function getBookingStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_payment: 'Aguardando pagamento',
    confirmed: 'Confirmada',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled_by_student: 'Cancelada pelo aluno',
    cancelled_by_instructor: 'Cancelada pelo instrutor',
    no_show: 'Não compareceu',
  }
  return labels[status] ?? status
}

/**
 * Returns a Tailwind CSS color class suitable for a status badge.
 */
export function getBookingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending_payment: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-brand-100 text-brand-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled_by_student: 'bg-red-100 text-red-800',
    cancelled_by_instructor: 'bg-red-100 text-red-800',
    no_show: 'bg-gray-100 text-gray-800',
  }
  return colors[status] ?? 'bg-gray-100 text-gray-800'
}
